import { isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  formatMeasured,
  type Column,
} from "../../components/index.ts";
import {
  containerLevel,
  containerStateLabel,
  dropReasonLabel,
  formatAgeSeconds,
  formatCount,
  formatRate,
  formatTime,
  quantityMeasure,
  type SystemHealth,
} from "./vocab.ts";

/**
 * Queue-ACCEPTANCE health (task 11.15; design 12.2 "queue depth and age, DLQ depth", 5.3 / 6.1
 * FR-14.6/14.7). This region answers ONE question: "is telemetry being ACCEPTED and MOVING?" — the
 * durable `202` side of ingest. It shows the container state, the queue depth and the age of its
 * oldest item, the dead-letter DEPTH (as the backlog of batches that gave up — the counts live
 * here; the per-batch failure DETAIL is a SEPARATE region), the ingest accept/reject rates, and the
 * drops in range broken down by reason.
 *
 * CRITICAL (the whole point of the screen): this region NEVER folds in a processing OUTCOME. A
 * batch's `202` acceptance is independent of whether it later processed — "the status row is written
 * separately from the ingest 202; no processing outcome ever changes the 202". So nothing here is a
 * single "success/fail" number that mixes acceptance with processing; acceptance is depth / age /
 * rates / drops, and processing failures are shown in their own region. The DLQ depth appears in
 * BOTH framings honestly: here as the acceptance-side backlog count, and in the processing-failures
 * region as the per-batch terminal state — they are the same underlying batches viewed two ways,
 * not merged into one number.
 *
 * Every tile is icon+text where it carries status (design 12.4); a null age/rate reads "n/a" (no
 * data), never a reassuring zero. Counts are exact; sampled rates carry "est." via the shared
 * `Quantity` → `Measure` mapping.
 */

export interface QueueAcceptanceProps {
  readonly container: SystemHealth["container"];
  readonly queue: SystemHealth["queue"];
  readonly deadLetter: SystemHealth["dead_letter"];
  readonly ingest: SystemHealth["ingest"];
  readonly ready: boolean;
}

export function QueueAcceptance(props: QueueAcceptanceProps): ReactNode {
  const navigate = useNavigate();
  const { container, queue, deadLetter, ingest, ready } = props;

  const dlqWarn = deadLetter.depth > 0;

  return (
    <section className="sys-region" aria-label="Queue acceptance health">
      <h2 className="sys-region__title">Queue acceptance</h2>
      <p className="sys-region__note sys-faint">
        Whether telemetry is being accepted into the durable queue and moving. A batch&rsquo;s
        acceptance is recorded independently of whether it later processed. Processing outcomes are
        shown separately below.
      </p>

      <div className="sys-tiles" role="group" aria-label="Queue acceptance tiles">
        <article className="sys-tile" aria-label="Container state">
          <h3 className="sys-tile__label">Container</h3>
          <StatusBadge
            level={containerLevel(container.state)}
            label={containerStateLabel(container.state)}
            title={
              container.state === "ready"
                ? "The control plane is running."
                : container.state === "waking"
                  ? "The control plane is waking from cold."
                  : "The control plane is unavailable."
            }
          />
          <p className="sys-tile__sub">
            {ready ? "Ready for ingest and reads" : "Not ready for ingest and reads"}
            {container.since !== null && (
              <>
                {" "}
                · since{" "}
                <time dateTime={isoAttr(container.since)}>
                  {formatTime(container.since)}
                </time>
              </>
            )}
          </p>
        </article>

        <article className="sys-tile" aria-label="Queue depth">
          <h3 className="sys-tile__label">Queue depth</h3>
          <p className="sys-tile__value">{formatCount(queue.depth)}</p>
          <p className="sys-tile__sub">
            oldest {formatAgeSeconds(queue.oldest_age_s)}
            {queue.oldest_age_s === null && " (no backlog)"}
          </p>
        </article>

        <article className="sys-tile" aria-label="Dead-letter depth" data-warn={dlqWarn || undefined}>
          <h3 className="sys-tile__label">Dead-letter depth</h3>
          {dlqWarn ? (
            <button
              type="button"
              className="sys-tile__value sys-link"
              data-warn="true"
              title="Batches that reached the dead-letter queue. See the per-batch processing failures below."
              onClick={scrollToFailures}
            >
              {formatCount(deadLetter.depth)}
            </button>
          ) : (
            <p className="sys-tile__value">{formatCount(deadLetter.depth)}</p>
          )}
          <p className="sys-tile__sub">
            oldest {formatAgeSeconds(deadLetter.oldest_age_s)}
            {deadLetter.depth === 0 && " (none dead-lettered)"}
          </p>
        </article>

        <article className="sys-tile" aria-label="Ingest accepted rate">
          <h3 className="sys-tile__label">Accepted</h3>
          <p className="sys-tile__value">
            {ingest.accepted_per_s.value === null
              ? "n/a"
              : formatMeasured(
                  { value: formatRate(ingest.accepted_per_s.value), measure: quantityMeasure(ingest.accepted_per_s) },
                )}
          </p>
          <p className="sys-tile__sub">durable 202 acceptances per second</p>
        </article>

        <article className="sys-tile" aria-label="Ingest rejected rate">
          <h3 className="sys-tile__label">Rejected</h3>
          <p className="sys-tile__value" data-warn={rejectWarn(ingest) || undefined}>
            {ingest.rejected_per_s.value === null
              ? "n/a"
              : formatMeasured(
                  { value: formatRate(ingest.rejected_per_s.value), measure: quantityMeasure(ingest.rejected_per_s) },
                )}
          </p>
          <p className="sys-tile__sub">rejected at admission per second</p>
        </article>

        <article className="sys-tile" aria-label="Drops in range">
          <h3 className="sys-tile__label">Drops in range</h3>
          {ingest.drops_in_range > 0 ? (
            <button
              type="button"
              className="sys-tile__value sys-link"
              data-warn="true"
              title="Dropped or altered telemetry in this window. See the affected sources."
              onClick={() => navigate("/sources")}
            >
              {formatCount(ingest.drops_in_range)}
            </button>
          ) : (
            <p className="sys-tile__value">0</p>
          )}
          <p className="sys-tile__sub">dropped or altered this window</p>
        </article>
      </div>

      <DropsByReason rows={ingest.drops_by_reason} />
    </section>
  );
}

/** The per-reason breakdown of drops in range, as a semantic table (design 12.4). Catalog order. */
function DropsByReason(props: {
  rows: SystemHealth["ingest"]["drops_by_reason"];
}): ReactNode {
  // Most-dropped first so the dominant cause is at the top; zero-filled reasons are not sent by the
  // server, so we show exactly what it reported (an empty list reads as "no drops").
  const rows = [...props.rows].sort((a, b) => b.count - a.count);

  const columns: readonly Column<SystemHealth["ingest"]["drops_by_reason"][number]>[] = [
    {
      key: "reason",
      header: "Drop reason",
      isRowHeader: true,
      cell: (r) => dropReasonLabel(r.reason),
    },
    {
      key: "count",
      header: "Count",
      align: "right",
      measure: "exact",
      cell: (r) => <span className="sys-num">{formatCount(r.count)}</span>,
    },
  ];

  return (
    <div className="sys-subregion">
      <h3 className="sys-subregion__title">Drops by reason</h3>
      <DenseTable
        caption="Dropped or altered telemetry in this window, by reason."
        columns={columns}
        rows={rows}
        rowKey={(r) => r.reason}
        emptyMessage="No telemetry was dropped or altered in this window."
      />
    </div>
  );
}

function rejectWarn(ingest: SystemHealth["ingest"]): boolean {
  return ingest.rejected_per_s.value !== null && ingest.rejected_per_s.value > 0;
}

/** Jump to the processing-failures region (the DLQ depth drills to the per-batch detail). */
function scrollToFailures(): void {
  const el = document.getElementById("sys-processing-failures");
  if (el !== null) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
